import type { AbuseCaseStatus, AbusePriority, DisclosureStatus, DisputeStatus, DomainActionStatus, LawEnforcementStatus } from '../../api';
import type { Meta } from '../domains/status';

/**
 * Labels are the Vietnamese the console shows when a caller renders a `Meta` directly (the
 * domain detail tabs do). Pages that go through `useEnumLabel` read the `compliance`
 * namespace instead, so both locales stay translatable.
 */

/** Abuse case states (spec §44) and the transitions the server allows. */
export const ABUSE_STATUS: Record<AbuseCaseStatus, Meta> = {
  NEW: { label: 'Mới', tone: 'info' },
  TRIAGED: { label: 'Đã phân loại', tone: 'pending' },
  INVESTIGATING: { label: 'Đang xác minh', tone: 'pending' },
  ACTION_REQUIRED: { label: 'Cần xử lý', tone: 'serious' },
  WAITING: { label: 'Đang chờ', tone: 'neutral' },
  RESOLVED: { label: 'Đã xử lý', tone: 'ok' },
  CLOSED: { label: 'Đã đóng', tone: 'neutral' },
};

export const ABUSE_TRANSITIONS: Record<AbuseCaseStatus, AbuseCaseStatus[]> = {
  NEW: ['TRIAGED', 'CLOSED'],
  TRIAGED: ['INVESTIGATING', 'ACTION_REQUIRED', 'WAITING', 'RESOLVED', 'CLOSED'],
  INVESTIGATING: ['ACTION_REQUIRED', 'WAITING', 'RESOLVED'],
  ACTION_REQUIRED: ['INVESTIGATING', 'WAITING', 'RESOLVED'],
  WAITING: ['INVESTIGATING', 'ACTION_REQUIRED', 'RESOLVED'],
  RESOLVED: ['INVESTIGATING', 'CLOSED'],
  CLOSED: [],
};

export const PRIORITY: Record<AbusePriority, Meta> = {
  LOW: { label: 'Thấp', tone: 'neutral' },
  MEDIUM: { label: 'Trung bình', tone: 'info' },
  HIGH: { label: 'Cao', tone: 'warn' },
  CRITICAL: { label: 'Rất cao', tone: 'crit' },
};

export const ABUSE_CATEGORIES = ['SPAM', 'PHISHING', 'MALWARE', 'BOTNET', 'PHARMING', 'FRAUD', 'CSAM', 'TRADEMARK', 'OTHER'] as const;
export const ABUSE_PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const;
export const ABUSE_SOURCES = ['REPORT', 'LAW_ENFORCEMENT', 'REGISTRY', 'ICANN', 'INTERNAL'] as const;
export const ABUSE_CHANNELS = ['WEB_FORM', 'EMAIL', 'API', 'PHONE', 'POSTAL', 'INTERNAL'] as const;
export const REPORTER_TYPES = ['ANONYMOUS', 'INDIVIDUAL', 'ORGANIZATION', 'LAW_ENFORCEMENT', 'REGISTRY', 'ICANN', 'INTERNAL'] as const;
export const LE_STATUSES: LawEnforcementStatus[] = ['RECEIVED', 'ACKNOWLEDGED', 'RESPONDED', 'REJECTED', 'CLOSED'];

export const LE_STATUS: Record<LawEnforcementStatus, Meta> = {
  RECEIVED: { label: 'Đã tiếp nhận', tone: 'info' },
  ACKNOWLEDGED: { label: 'Đã xác nhận', tone: 'pending' },
  RESPONDED: { label: 'Đã trả lời', tone: 'ok' },
  REJECTED: { label: 'Đã từ chối', tone: 'warn' },
  CLOSED: { label: 'Đã đóng', tone: 'neutral' },
};

export const DOMAIN_ACTION_STATUS: Record<DomainActionStatus, Meta> = {
  PENDING: { label: 'Đang chờ', tone: 'pending' },
  APPLIED: { label: 'Đã áp dụng', tone: 'ok' },
  FAILED: { label: 'Thất bại', tone: 'crit' },
  UNKNOWN: { label: 'Chưa rõ kết quả', tone: 'serious' },
};

/** Dispute states (spec §45) and allowed transitions. */
export const DISPUTE_STATUS: Record<DisputeStatus, Meta> = {
  OPENED: { label: 'Đã mở', tone: 'info' },
  LOCKED: { label: 'Đã khoá tên miền', tone: 'warn' },
  DECIDED: { label: 'Đã có phán quyết', tone: 'pending' },
  IMPLEMENTED: { label: 'Đã thực hiện phán quyết', tone: 'pending' },
  WITHDRAWN: { label: 'Đã rút', tone: 'neutral' },
  CLOSED: { label: 'Đã đóng', tone: 'ok' },
};

export const DISPUTE_TYPES = ['UDRP', 'URS', 'COURT_ORDER', 'REGISTRY_ACTION', 'GOVERNMENT_REQUEST', 'OTHER'] as const;
export const DISPUTE_DECISIONS = ['TRANSFER', 'CANCELLATION', 'SUSPENSION', 'DENIED', 'SETTLED', 'OTHER'] as const;

/** Disclosure case states (spec §41). */
export const DISCLOSURE_STATUS: Record<DisclosureStatus, Meta> = {
  RECEIVED: { label: 'Đã tiếp nhận', tone: 'info' },
  UNDER_REVIEW: { label: 'Đang xem xét', tone: 'pending' },
  INFO_REQUESTED: { label: 'Đã yêu cầu bổ sung', tone: 'warn' },
  APPROVED: { label: 'Đã chấp thuận', tone: 'ok' },
  PARTIALLY_APPROVED: { label: 'Chấp thuận một phần', tone: 'ok' },
  DENIED: { label: 'Đã từ chối', tone: 'neutral' },
  RELEASED: { label: 'Đã cung cấp', tone: 'ok' },
  CLOSED: { label: 'Đã đóng', tone: 'neutral' },
};

export const DISCLOSURE_REQUESTER_TYPES = ['LAW_ENFORCEMENT', 'IP_RIGHTS_HOLDER', 'CYBERSECURITY', 'CONSUMER_PROTECTION', 'LEGAL_PROCEEDING', 'OTHER'] as const;
